import {
	NgForOf,
	NgIf,
	NgSwitch,
	NgSwitchCase,
	NgSwitchDefault
} from '@angular/common';
import { Component, HostBinding, Input, Optional } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TypographyComponent } from 'components';
import { ChartWidgetComponent } from '../chart-widget/chart-widget.component';
import { TableWidgetComponent } from '../table-widget/table-widget.component';
import {
	ChartType,
	ChartWidget,
	TableWidget
} from '../../../../models/preview/preview.models';
import { ReportBlockData } from '../../../../services/report-blocks.service';
import { recolorWidgetInPlace } from '../../../../models/preview/preview-overrides.models';
import {
	REPORT_DEFAULT_ACCENT_COLOR,
	normalizeThemeColor
} from '../../../../services/report-preview-utils';
import {
	assignEntityColors,
	blockDayMarks,
	blockEntityNames,
	blockFigures,
	blockTable,
	buildBlockChartConfig,
	ENTITY_FIGURE_SEPARATOR
} from '../../../../services/report-block-chart-config';
import {
	formatReportDay,
	numberSource,
	ReportSource,
	reportSourceDetail
} from '../../../../services/report-source';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';

/** One entity's headline figures, under its own name and colour. */
interface BlockFigureGroup {
	/** Whose these are, or null when the block is about one thing only. */
	entity: string | null;
	/** Its chart colour, so the tiles and the charts name it the same way. */
	color: string | null;
	/** `source` is where that one figure came from, for its tooltip. */
	figures: Array<{ label: string; value: string; source: string }>;
}

/** A marked day's line under the chart, as its mark on the chart numbers it. */
interface BlockDayMarkLine {
	number: string;
	/** "12 Sep · 320 mentions, 8x a normal day in this window (40)". */
	heading: string;
	/** What carried the day. */
	text: string;
}

type DrawingKind = 'empty' | 'kpi' | 'table' | 'chart' | 'unknown';

/**
 * One set of numbers, drawn as whatever type was chosen for it.
 *
 * The one renderer behind every switchable chart: the charts a reader added,
 * and the chart inside a fixed section once it is switched away from its own
 * design. Both hand it a resolved block — the numbers already on the page —
 * and it draws a chart, a table or a row of stat cards from them without
 * asking for anything again, which is what makes a switch instant.
 *
 * Charts go through the chart widget, so they get the same degrade rules as
 * every chart in the report (a series too short for a line becomes bars, a
 * lone point shows, an area keeps its fade), and they are painted from the
 * shared palette with the deck's accent swapped for the report's theme, the
 * way the fixed sections are.
 *
 * An empty block prints its reason. A chart with no bars and a chart that
 * failed look identical to a reader, and "nothing was collected from web
 * sources in this period" is the useful half of that answer.
 */
@Component({
	standalone: true,
	selector: 'app-report-chart-drawing',
	imports: [
		NgIf,
		NgForOf,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		MatTooltipModule,
		TypographyComponent,
		ChartWidgetComponent,
		TableWidgetComponent
	],
	template: `
		<ng-container [ngSwitch]="kind">
			<div class="drawing__empty" *ngSwitchCase="'empty'">
				<typography variant="p/sm_regular">
					{{ emptyText }}
				</typography>
			</div>

			<div class="drawing__figures" *ngSwitchCase="'kpi'">
				<div class="figure-group" *ngFor="let group of figureGroups">
					<div class="figure-group__head" *ngIf="group.entity">
						<span
							class="figure-group__dot"
							[style.background]="group.color"
						></span>
						<typography variant="p/xs_semibold">
							{{ group.entity }}
						</typography>
					</div>

					<div class="figure-group__tiles">
						<div
							class="drawing__figure"
							*ngFor="let figure of group.figures"
							[matTooltip]="figure.source"
							[matTooltipDisabled]="!figure.source"
							matTooltipPosition="above"
							matTooltipClass="report-source-tooltip"
						>
							<typography
								variant="p/xs_regular"
								class="figure__label"
							>
								{{ figure.label }}
							</typography>
							<typography variant="h/3" class="figure__value">
								{{ figure.value }}
							</typography>
						</div>
					</div>
				</div>
			</div>

			<div class="drawing__table" *ngSwitchCase="'table'">
				<app-table-widget [widget]="tableWidget"></app-table-widget>
			</div>

			<ng-container *ngSwitchCase="'chart'">
				<app-chart-widget
					class="drawing__chart"
					[widget]="chartWidget"
				></app-chart-widget>

				<!-- TRAP: listed under the chart, never in a tooltip — the
				     PDF has no hover to open one. -->
				<ol
					class="drawing__marks"
					*ngIf="dayMarks.length"
					aria-label="Marked days"
				>
					<li class="drawing__mark" *ngFor="let mark of dayMarks">
						<span
							class="drawing__mark-number"
							aria-hidden="true"
							[style.background]="markColor"
						>
							<typography variant="p/xxs_semibold">
								{{ mark.number }}
							</typography>
						</span>
						<div class="drawing__mark-body">
							<typography variant="p/xs_semibold">
								{{ mark.heading }}
							</typography>
							<typography
								variant="p/xs_regular"
								class="drawing__mark-text"
							>
								{{ mark.text }}
							</typography>
						</div>
					</li>
				</ol>
			</ng-container>

			<div class="drawing__empty" *ngSwitchDefault>
				<typography variant="p/sm_regular">
					{{ cannotDrawText }}
				</typography>
			</div>
		</ng-container>
	`,
	styles: [
		`
			:host {
				display: block;
				width: 100%;
			}

			/* In a fixed section: the chart area is already sized, so the
			   drawing takes all of it and never grows the slide — a long
			   table scrolls in the editor rather than pushing the slide's
			   footer off its clipped edge. */
			:host(.drawing--fill) {
				height: 100%;
				min-height: 0;
			}

			.drawing__chart {
				display: block;
				width: 100%;
				height: 18rem;
			}

			:host(.drawing--fill) .drawing__chart {
				height: 100%;
			}

			/* TRAP: the chart widget's pie square is 320px, taller than this
			   18rem box, and its legend is drawn at the bottom of the canvas —
			   so a pie with many slices ran its legend over the source line
			   below the box. Kept inside the box, the legend shrinks the pie. */
			.drawing__chart ::ng-deep .chart-wrapper--pie {
				height: 100%;
				min-height: 0;
			}

			/* The chart widget sizes a pie to a fixed square for the added
			   charts' cards; inside a sized chart area it fills it instead. */
			:host(.drawing--fill) ::ng-deep .chart-wrapper,
			:host(.drawing--fill) ::ng-deep .chart-wrapper--pie {
				height: 100%;
				min-height: 0;
				max-width: none;
			}

			:host(.drawing--fill) .drawing__table,
			:host(.drawing--fill) .drawing__figures {
				height: 100%;
				overflow: auto;
			}

			/* The PDF has no scrolling, and a scrollbar printed into a slide
			   is chrome, not content. After the rule above, which it has to
			   win against. */
			:host-context(.report-export-mode) .drawing__table,
			:host-context(.report-export-mode) .drawing__figures {
				overflow: hidden;
			}

			.drawing__table {
				width: 100%;
			}

			.drawing__figures {
				display: flex;
				flex-direction: column;
				gap: 1.25rem;
				width: 100%;
			}

			.figure-group {
				display: flex;
				flex-direction: column;
				gap: 0.5rem;
			}

			.figure-group__head {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				color: var(--report-value, #333a42);
			}

			.figure-group__dot {
				width: 0.5rem;
				height: 0.5rem;
				border-radius: 50%;
				flex: none;
			}

			/* A grid rather than a wrapping row. Comparing doubles the tiles —
			   four metrics each for two brands — and a flex wrap put five on
			   the first line and three on the second, which reads as eight
			   unrelated numbers. One row per entity of identical tracks puts
			   each metric under its own counterpart, which is the comparison.
			   Empty tracks collapse, so a single-entity block of four figures
			   still gets four columns and not a quarter-empty row. */
			.figure-group__tiles {
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
				gap: 1rem;
			}

			.drawing__figure {
				display: flex;
				flex-direction: column;
				gap: 0.25rem;
				/* Grid items are auto-width by default, which a long value
				   pushes past its column. */
				min-width: 0;
			}

			.figure__label {
				opacity: 0.6;
			}

			.drawing__empty {
				width: 100%;
				justify-content: center;
				display: flex;
				opacity: 0.6;
			}

			.drawing__marks {
				display: flex;
				flex-direction: column;
				gap: 0.5rem;
				margin: 0.75rem 0 0;
				padding: 0;
				list-style: none;

				.drawing__mark {
					display: flex;
					align-items: flex-start;
					gap: 0.5rem;

					.drawing__mark-number {
						display: inline-flex;
						flex: none;
						align-items: center;
						justify-content: center;
						width: 1.125rem;
						height: 1.125rem;
						border-radius: 50%;
						color: var(--report-chart-surface, #ffffff);
					}

					.drawing__mark-body {
						display: flex;
						flex-direction: column;
						gap: 0.125rem;
						min-width: 0;
						color: var(--report-value, #333a42);

						.drawing__mark-text {
							opacity: 0.75;
						}
					}
				}
			}
		`
	]
})
export class ReportChartDrawingComponent {
	@Input() set block(value: ReportBlockData | null) {
		this._block = value;
		this.rebuild();
	}

	get block(): ReportBlockData | null {
		return this._block;
	}

	// A setter too, so the drawing is repainted when the report is, and not
	// only when its numbers change.
	@Input() set themeColor(value: string | null) {
		this._themeColor = normalizeThemeColor(value);
		this.rebuild();
	}

	/**
	 * Fill the box it is put in — a fixed section's chart area — rather than
	 * size itself the way an added chart's card does.
	 */
	@HostBinding('class.drawing--fill')
	@Input()
	fill = false;

	/**
	 * Where the numbers came from, so each stat card can say so in its own
	 * tooltip. Null draws the cards without one — a fixed section switched to
	 * stat cards has its source line on the slide already.
	 */
	@Input() set source(value: ReportSource | null) {
		this._source = value;
		this.refreshFigureSources();
	}

	kind: DrawingKind = 'empty';
	emptyText = '';
	cannotDrawText = '';
	chartWidget!: ChartWidget;
	/** The days a trend marks, in their marks' order; empty for every other chart. */
	dayMarks: BlockDayMarkLine[] = [];
	/** The marks' colour: the line's, which is the report's theme. */
	markColor = REPORT_DEFAULT_ACCENT_COLOR;
	tableWidget!: TableWidget;
	figureGroups: BlockFigureGroup[] = [];

	private _block: ReportBlockData | null = null;
	private _themeColor: string | null = null;
	private _source: ReportSource | null = null;

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	private rebuild(): void {
		const block = this._block;
		const language = this.reportLanguage?.language;
		this.emptyText =
			block?.note || reportMessage(language, 'empty.nothing');
		this.cannotDrawText = reportMessage(language, 'empty.cannot_draw');
		this.dayMarks = [];
		if (!block || block.empty) {
			this.kind = 'empty';
			return;
		}

		if (block.render === 'kpi') {
			this.figureGroups = this.buildFigureGroups(block);
			this.refreshFigureSources();
			this.kind = this.figureGroups.length ? 'kpi' : 'empty';
			return;
		}

		if (block.render === 'table') {
			this.tableWidget = this.buildTableWidget(block);
			this.applyTheme(this.tableWidget);
			this.kind = this.tableWidget.content.rows.length
				? 'table'
				: 'empty';
			return;
		}

		const config = buildBlockChartConfig(block);
		if (!config) {
			this.kind = 'unknown';
			return;
		}
		this.chartWidget = {
			id: `${block.block_id}-chart`,
			kind: 'chart',
			visible: true,
			content: {
				// What the chart IS, not what was asked for: a compared
				// doughnut is drawn as a grouped bar, and a widget that
				// disagreed with its own config would be a trap for whoever
				// reads this next.
				chartType: config.type as unknown as ChartType,
				dataSourceKey: block.block_id,
				chartJsConfig: config as unknown as Record<string, unknown>
			}
		};
		this.applyTheme(this.chartWidget);
		this.markColor = this._themeColor ?? REPORT_DEFAULT_ACCENT_COLOR;
		this.dayMarks =
			block.render === 'ranked_bar'
				? []
				: blockDayMarks(block).map(mark => ({
						number: mark.number,
						heading: `${formatReportDay(
							mark.annotation.date,
							language
						)} · ${mark.annotation.label}`,
						text: mark.annotation.text
				  }));
		this.kind = 'chart';
	}

	/**
	 * A drawing is painted from the shared palette, whose first entry is the
	 * deck's default accent — so the same accent → theme substitution the fixed
	 * slides get is all this needs. A series that landed on one of the other
	 * palette entries keeps it, which is what keeps two series on one chart
	 * telling apart.
	 */
	private applyTheme(widget: ChartWidget | TableWidget): void {
		const theme = this._themeColor;
		if (theme && theme !== REPORT_DEFAULT_ACCENT_COLOR) {
			recolorWidgetInPlace(widget, REPORT_DEFAULT_ACCENT_COLOR, theme);
		}
	}

	/**
	 * The headline figures, grouped by whose they are.
	 *
	 * A compared KPI block arrives with its figures prefixed — "Costco ·
	 * Mentions", "Sam's Club · Mentions" — so four metrics become eight tiles.
	 * Laid out flat that interleaves two brands into one run of numbers where
	 * the reader has to read every label to find the pair that belongs
	 * together, and eight tiles of a 3-4 tile layout is also what threatens the
	 * fixed 823px page. Grouping fixes both: each brand's figures sit on their
	 * own line under their own name, and four entities (the backend's cap) come
	 * to roughly 450px.
	 *
	 * The prefix is split off each key rather than each entity's figures being
	 * picked out by name, so a figure whose key does not carry an entity still
	 * lands somewhere instead of being silently dropped.
	 */
	private buildFigureGroups(block: ReportBlockData): BlockFigureGroup[] {
		const figures = Object.entries(blockFigures(block));
		if (!figures.length) {
			return [];
		}

		const entities = blockEntityNames(block);
		const comparing =
			entities.length > 1 && Object.keys(block.figures ?? {}).length > 0;
		const colors = comparing ? assignEntityColors(entities) : null;
		const groups: BlockFigureGroup[] = [];
		const byEntity = new Map<string, BlockFigureGroup>();

		for (const [key, value] of figures) {
			const at = comparing ? key.indexOf(ENTITY_FIGURE_SEPARATOR) : -1;
			const entity = at > 0 ? key.slice(0, at) : null;
			// The entity heads the group now, so repeating it on every tile
			// would only narrow the tile.
			const label =
				at > 0 ? key.slice(at + ENTITY_FIGURE_SEPARATOR.length) : key;

			let group = byEntity.get(entity ?? '');
			if (!group) {
				group = {
					entity,
					color: entity ? colors?.get(entity) ?? null : null,
					figures: []
				};
				byEntity.set(entity ?? '', group);
				groups.push(group);
			}
			// Its source is filled in by refreshFigureSources, which also runs
			// when the source arrives after the numbers.
			group.figures.push({ label, value, source: '' });
		}

		return groups;
	}

	/**
	 * Each stat card's own source. A figure of a compared block names only its
	 * own entity — the card sits under that entity's heading, and a tooltip
	 * listing all four brands for one number would say less, not more.
	 */
	private refreshFigureSources(): void {
		const source = this._source;
		for (const group of this.figureGroups) {
			for (const figure of group.figures) {
				figure.source = source
					? reportSourceDetail(
							numberSource(source, figure.label, group.entity)
					  )
					: '';
			}
		}
	}

	private buildTableWidget(block: ReportBlockData): TableWidget {
		const table = blockTable(block);
		return {
			id: `${block.block_id}-table`,
			kind: 'table',
			visible: true,
			content: {
				dataSourceKey: block.block_id,
				columns: table.columns.map(column => ({
					key: column.key,
					label: column.label,
					kind: column.kind
				})),
				rows: table.rows as unknown as Array<Record<string, string>>
			}
		};
	}
}
